<template>
  <div v-if="isLoadingAuth || isLoading" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70">
    <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
  </div>

  <!-- Not logged in -->
  <div v-else-if="!user" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70">
    <div class="flex items-center gap-3 bg-gray-800 border border-gray-700 px-4 py-3 rounded-md">
      <i class="fas fa-exclamation-triangle text-2xl text-red-400"></i>
      <span class="text-lg font-semibold text-indigo-200">กรุณาเข้าสู่ระบบ</span>
    </div>
  </div>

  <!-- Main Modal -->
  <div v-else class="fixed inset-0 bg-gray-900 bg-opacity-70 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-modalFade">
    <div class="relative w-full max-w-lg sm:max-w-2xl bg-gray-800 text-gray-300 rounded-lg p-4 sm:p-6 flex flex-col gap-4 shadow-lg border border-gray-700 animate-scaleIn">

      <!-- Error & Success -->
      <div v-if="error" class="p-2 bg-red-900/60 text-red-300 text-center text-sm rounded-md border border-red-800">
        {{ error }}
      </div>
      <div v-if="successMessage" class="p-2 bg-green-900/60 text-green-300 text-center text-sm rounded-md border border-green-800">
        {{ successMessage }}
      </div>

      <!-- Header -->
      <div class="flex justify-between items-start sm:items-center border-b border-gray-700 pb-3">
        <div class="flex items-start sm:items-center gap-3">
          <i class="fas fa-warehouse text-indigo-300 text-2xl"></i>
          <div class="space-y-0.5">
            <h1 class="text-lg font-semibold text-indigo-300">ย้ายไอเทมไปยังคลัง</h1>
            <p class="text-indigo-300 text-xs">เลือกไอเทมที่ต้องการย้าย</p>
            <p class="text-indigo-300 text-xs">น้ำหนักรวมที่เลือก: <strong>{{ selectedWeight }}</strong></p>
          </div>
        </div>
        <button @click="closeModal" class="text-gray-300 hover:text-indigo-300 transition-colors duration-200">
          <i class="fas fa-times text-base"></i>
        </button>
      </div>

      <!-- Item List -->
      <div class="max-h-96 overflow-y-auto p-2" id="style-1">
        <div v-if="isLoading" class="text-center text-indigo-200 py-4 text-sm">กำลังโหลดไอเทม...</div>
        <div v-else-if="items.length === 0" class="text-center text-indigo-200 py-4 text-sm">ไม่พบไอเทมในกระเป๋า</div>

        <transition-group
          v-else
          name="item"
          tag="div"
          class="grid grid-cols-2 sm:grid-cols-3 gap-1.5"
        >
          <div
            v-for="item in items"
            :key="item.id"
            @click="toggleItemSelection(item)"
            :class="[
              'cursor-pointer rounded-md border transition-all duration-200 relative p-1.5',
              selectedItems.includes(item.id)
                ? 'bg-indigo-700/50 border-indigo-600 ring-1 ring-indigo-700'
                : 'bg-gray-800 border-gray-700 hover:bg-gray-700 hover:border-indigo-600'
            ]"
            role="button"
            :aria-label="`เลือกไอเทม ${item.name}`"
          >
            <div class="flex items-center gap-2">
              <img
                :src="item.img || 'default-item.png'"
                :alt="item.name"
                class="w-9 h-9 rounded-md object-cover border-2"
                :class="[
                  item.rarityColors?.borderColor || 'border-gray-600',
                  item.rarityColors?.bgColor || 'bg-gray-800'
                ]"
                @error="handleImageError"
              />
              <div class="flex-1 min-w-0">
                <h3 class="text-xs sm:text-sm font-semibold text-indigo-300 truncate">{{ item.name }}</h3>
                <p class="text-xs text-indigo-20 truncate">ประเภท: {{ item.type }}</p>
                <p class="text-xs text-indigo-200">น้ำหนัก: {{ item.weight || 0 }}</p>
              </div>

              <input
                type="checkbox"
                :checked="selectedItems.includes(item.id)"
                class="absolute top-2 right-2 h-4 w-4 text-indigo-600 focus:ring-indigo-700 border-gray-600 rounded bg-gray-800"
                @click.stop="toggleItemSelection(item)"
                :aria-label="`เลือก ${item.name}`"
              />
            </div>
          </div>
        </transition-group>
      </div>

      <!-- Back to Top -->
      <button
        v-if="showBackToTop"
        @click="scrollToTop"
        class="fixed bottom-20 right-6 bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-3 rounded-md transition-colors duration-200 border border-indigo-700"
      >
        <i class="fas fa-arrow-up text-sm"></i>
      </button>

      <!-- Action Bar -->
      <div class="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2 border-t border-gray-700 pt-3">
        <div class="flex gap-2">
          <button
            v-if="items.length > 0"
            @click="selectAllItems"
            :disabled="isMoving || items.length === 0"
            :class="[
              'bg-blue-600 hover:bg-blue-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-blue-700',
              { 'opacity-60 cursor-not-allowed': isMoving || items.length === 0 }
            ]"
          >
            <i class="fas fa-check-square text-xs"></i> เลือกทั้งหมด
          </button>
          <button
            v-if="selectedItems.length > 0"
            @click="deselectAllItems"
            :disabled="isMoving || selectedItems.length === 0"
            :class="[
              'bg-gray-700 hover:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-gray-600',
              { 'opacity-60 cursor-not-allowed': isMoving || selectedItems.length === 0 }
            ]"
          >
            <i class="fas fa-times-circle text-xs"></i> ยกเลิกเลือกทั้งหมด
          </button>
        </div>

        <div class="flex gap-2">
          <button
            v-if="selectedItems.length > 0"
            @click="confirmMove = true"
            :disabled="isMoving || selectedItems.length === 0"
            :class="[
              'bg-indigo-600 hover:bg-indigo-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-indigo-700',
              { 'opacity-60 cursor-not-allowed': isMoving || selectedItems.length === 0 }
            ]"
          >
            <span v-if="!isMoving"><i class="fas fa-warehouse text-xs"></i> ย้ายไปยังคลัง ({{ selectedItems.length }})</span>
            <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
          </button>

          <button
            @click="closeModal"
            class="flex items-center bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-gray-600 text-sm"
          >
            <i class="fas fa-times mr-1 text-sm"></i> ปิด
          </button>
        </div>
      </div>

      <!-- Confirmation Modal -->
      <transition name="modal">
        <div
          v-if="confirmMove"
          class="absolute inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center rounded-lg animate-modalFade"
        >
          <div class="bg-gray-800 rounded-lg w-full max-w-md shadow-lg border border-gray-700 animate-scaleIn">
            <div class="p-4 border-b border-gray-700 flex justify-between items-center">
              <h1 class="text-lg font-semibold text-indigo-300">ยืนยันการย้ายไอเทม</h1>
              <button @click="confirmMove = false" class="text-gray-300 hover:text-indigo-300 transition-colors duration-200">
                <i class="fas fa-times text-base"></i>
              </button>
            </div>
            <div class="p-4 text-sm text-indigo-200">
              คุณแน่ใจหรือไม่ว่าต้องการย้าย {{ selectedItems.length }} ไอเทม (น้ำหนักรวม {{ selectedWeight }}) ไปยังคลัง?
            </div>
            <div class="p-4 border-t border-gray-700 flex justify-end gap-2">
              <button
                @click="moveItemsToStorage"
                :disabled="isMoving"
                class="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md font-semibold transition-colors duration-200 border border-indigo-700 text-sm"
                :class="{ 'opacity-60 cursor-not-allowed': isMoving }"
              >
                <span v-if="!isMoving"><i class="fas fa-check mr-1 text-sm"></i> ยืนยัน</span>
                <i v-else class="fas fa-spinner animate-spin text-sm"></i>
              </button>
              <button
                @click="confirmMove = false"
                class="flex items-center bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-semibold transition-colors duration-200 border border-gray-600 text-sm"
              >
                <i class="fas fa-times mr-1 text-sm"></i> ยกเลิก
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Moving overlay -->
      <transition name="fade">
        <div
          v-if="isMoving"
          class="absolute inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center rounded-lg"
        >
          <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
        </div>
      </transition>

    </div>
  </div>
</template>

<script>
import getUser from '@/composables/getUser';
import { projectFunctions } from '@/firebase/config';
import { ref, computed, onMounted } from 'vue';

export default {
  name: 'MoveToStorageModal',
  props: {
    items: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  emits: ['close', 'items-moved'],
  setup(props, { emit }) {
    const { user, isLoadingAuth } = getUser();
    const isLoading = ref(false);
    const error = ref(null);
    const successMessage = ref('');
    const selectedItems = ref([]);
    const confirmMove = ref(false);
    const isMoving = ref(false);
    const showBackToTop = ref(false);

    // Compute total weight of selected items
    const selectedWeight = computed(() => {
      return selectedItems.value.reduce((sum, itemId) => {
        const item = props.items.find(i => i.id === itemId);
        return sum + (item ? (item.weight || 0) : 0);
      }, 0);
    });

    // Handle image error
    const handleImageError = (event) => {
      event.target.src = 'default-item.png';
    };

    // Toggle item selection
    const toggleItemSelection = (item) => {
      if (!item?.id) return;
      const index = selectedItems.value.indexOf(item.id);
      if (index === -1) {
        selectedItems.value.push(item.id);
      } else {
        selectedItems.value.splice(index, 1);
      }
      error.value = '';
      successMessage.value = '';
      console.log('Selected items:', selectedItems.value);
    };

    // Select all items
    const selectAllItems = () => {
      selectedItems.value = props.items.map(item => item.id);
      error.value = '';
      successMessage.value = '';
      console.log('All items selected:', selectedItems.value);
    };

    // Deselect all items
    const deselectAllItems = () => {
      selectedItems.value = [];
      error.value = '';
      successMessage.value = '';
      console.log('All items deselected');
    };

    // Move selected items to storage
    const moveItemsToStorage = async () => {
      if (!user.value || selectedItems.value.length === 0) {
        error.value = 'กรุณาเลือกไอเทมเพื่อย้าย';
        return;
      }
      isMoving.value = true;
      error.value = '';
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const response = await gameActions({
          action: 'moveItemsToStorage',
          instanceIds: selectedItems.value,
        });
        console.log('Move to storage response:', response);
        successMessage.value = `ย้าย ${selectedItems.value.length} ไอเทมไปยังคลังสำเร็จ!`;
        selectedItems.value = [];
        confirmMove.value = false;
        emit('items-moved');
        setTimeout(() => {
          emit('close');
          successMessage.value = '';
        }, 1000); // Delay closing to show success message
      } catch (err) {
        console.error('Error moving items to storage:', err);
        error.value = err.message || 'เกิดข้อผิดพลาดในการย้ายไอเทม';
      } finally {
        isMoving.value = false;
      }
    };

    // Close modal
    const closeModal = () => {
      selectedItems.value = [];
      error.value = '';
      successMessage.value = '';
      confirmMove.value = false;
      emit('close');
    };

    // Scroll to top
    const scrollToTop = () => {
      const container = document.querySelector('.max-h-96');
      if (container) {
        container.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    // Monitor scroll for back-to-top button
    const handleScroll = () => {
      const container = document.querySelector('.max-h-96');
      if (container) {
        showBackToTop.value = container.scrollTop > 100;
      }
    };

    onMounted(() => {
      const container = document.querySelector('.max-h-96');
      if (container) {
        container.addEventListener('scroll', handleScroll);
      }
    });

    return {
      user,
      isLoadingAuth,
      isLoading,
      error,
      successMessage,
      selectedItems,
      confirmMove,
      isMoving,
      showBackToTop,
      selectedWeight,
      handleImageError,
      toggleItemSelection,
      selectAllItems,
      deselectAllItems,
      moveItemsToStorage,
      closeModal,
      scrollToTop,
    };
  },
};
</script>

<style scoped>
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

.animate-spin-pulse {
  animation: spin-pulse 1.2s ease-in-out infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes spin-pulse {
  0% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.2); }
  100% { transform: rotate(360deg) scale(1); }
}

@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}

.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}

@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}

@keyframes fade {
  from { opacity: 0; }
  to { opacity: 1; }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.item-enter-active,
.item-leave-active {
  transition: all 0.3s ease;
}

.item-enter-from,
.item-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

button:not(:disabled):active {
  transform: scale(0.98);
}

input:focus {
  outline: none;
  box-shadow: 0 0 0 2px #6366f1;
}
</style>
